import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface UseUpdateUserEmailOptions {
  onSuccess?: (data: any) => void;
  onError?: (error: string) => void;
}

export const useUpdateUserEmail = (options?: UseUpdateUserEmailOptions) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const mutation = useMutation({
    mutationFn: async ({ userId, newEmail }: { userId: string; newEmail: string }) => {
      const { error } = await supabase.rpc('update_user_email', {
        _user_id: userId,
        _new_email: newEmail
      });

      if (error) {
        console.error('Error updating user email:', error);
        throw new Error(error.message);
      }

      return { success: true, userId, newEmail };
    },
    onSuccess: (result) => {
      // Show success toast
      toast({
        title: 'Success',
        description: 'User email updated successfully',
      });

      // Invalidate relevant queries
      queryClient.invalidateQueries({
        queryKey: ['superadmin-users'],
        refetchType: 'all'
      });

      // Call success callback if provided
      if (options?.onSuccess) {
        options.onSuccess(result);
      }
    },
    onError: (error: Error) => {
      // Show error toast
      toast({
        title: 'Error',
        description: error.message || 'Failed to update user email',
        variant: 'destructive',
      });

      // Call error callback if provided
      if (options?.onError) {
        options.onError(error.message);
      }
    },
  });

  const updateUserEmail = (userId: string, newEmail: string) => {
    mutation.mutate({ userId, newEmail });
  };

  return {
    updateUserEmail,
    isLoading: mutation.isPending,
    error: mutation.error,
  };
}; 